// Screening-template additional questions (BSCPF-6641). The structural helpers
// for conditional questions (grouping, reordering, inserting, removing) are
// shared with the CRM pre-screening builder; what differs here is how a
// question's type and answers are stored: `config.widget_type` and the
// applicant's literal "Yes"/"No" answers.
import { v4 as uuidv4 } from "uuid";
import {
  Question,
  QuestionWidgetType,
  Template
} from "types/landlord/verificationTemplate";
import { ApplicantType, VerificationNames } from "shared/types";
import { getChildren } from "shared/app/ListingTemplate/utils/conditionalQuestions";

// A blank Name defaults to this many characters of the question, like CRM.
// There are no length or option-count limits for now (product call).
export const NAME_MAX_LENGTH = 100;

// The builder works on questions that always have an id: conditional
// questions point at their parent by id, and ids key the draggable cards.
export type BuilderQuestion = Question & { id: string };

const YES_NO_TRIGGERS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" }
];

const newQuestionId = () => `q_${uuidv4().replace(/-/g, "")}`;

// Questions saved before question types existed have no widget type; the
// applicant sees them as plain text, so the builder opens them as Plain text.
export const getWidgetType = (question: Question): QuestionWidgetType =>
  question.config?.widget_type ?? "text";

export const canHaveConditionals = (widgetType: QuestionWidgetType) =>
  widgetType === "yes-no" || widgetType === "select";

export const getTriggerOptions = (
  question: Question
): { value: string; label: string }[] => {
  const widgetType = getWidgetType(question);
  if (widgetType === "yes-no") return YES_NO_TRIGGERS;
  if (widgetType === "select")
    return (question.config?.options ?? []).map((option) => ({
      value: option,
      label: option
    }));
  return [];
};

// Conditional questions of `parent` that its current type and answers can no
// longer show: all of them once it isn't Yes/No or Options, otherwise the ones
// whose trigger answer is gone.
export const getOrphanedChildIds = (
  questions: BuilderQuestion[],
  parent: BuilderQuestion
): string[] => {
  const triggers = canHaveConditionals(getWidgetType(parent))
    ? getTriggerOptions(parent).map((option) => option.value)
    : [];
  return getChildren(questions, parent.id)
    .filter((child) => !triggers.includes(child.parent_answer ?? ""))
    .map((child) => child.id);
};

// New questions start as a not-required Yes/No, like CRM.
export const createQuestion = (
  conditional?: Pick<Question, "parent_question_id" | "parent_answer">
): BuilderQuestion => ({
  id: newQuestionId(),
  name: "",
  title: "",
  type: "string",
  required: false,
  config: { widget_type: "yes-no" },
  ...conditional
});

// Legacy questions get an id the first time the builder opens them; it is
// saved with the template.
export const withId = (question: Question): BuilderQuestion => ({
  ...question,
  id: question.id || newQuestionId()
});

// The name a question is saved under: a blank Name becomes the start of the
// question (see the save cleanup in helpers.ts).
export const effectiveName = ({ name, title }: Question) =>
  name.trim() || title.trim().slice(0, NAME_MAX_LENGTH);

export const findDuplicateNames = (questions: Question[]): Set<string> => {
  const counts = new Map<string, number>();
  questions.forEach((question) => {
    const key = effectiveName(question);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return new Set([...counts].filter(([, n]) => n > 1).map(([key]) => key));
};

export const countAdditionalQuestions = ({
  template,
  partnerType,
  verificationName
}: {
  template: Template | null;
  partnerType: ApplicantType;
  verificationName:
    | VerificationNames.HousingHistory
    | VerificationNames.CombinedIncome;
}): number =>
  template?.verification_schemas[partnerType]?.[verificationName]
    ?.additional_questions?.length ?? 0;
